export function RealWorldCaseStudies() {
  const caseStudies = [
    {
      title: 'Case Study 1: The Renovation Disaster',
      location: 'Phoenix, Arizona',
      property: '24-Unit Apartment Complex',
      outcome: 'negative',
      details: {
        initial: 'Owner decided to renovate before selling',
        budget: '$600,000 renovation budget',
        timeline: 'Expected 8 months, took 16 months',
        issues: [
          'Discovered asbestos requiring $80,000 abatement',
          'Contractor went bankrupt mid-project',
          'Permit delays added 4 months',
          'Cost overruns reached $320,000',
          'Lost $288,000 in rental income during vacancy'
        ],
        result: 'Total investment: $1,208,000',
        value: 'Property value increased by only $180,000',
        loss: 'Net loss of $1,028,000'
      }
    },
    {
      title: 'Case Study 2: The Smart AS IS Sale',
      location: 'Dallas, Texas',
      property: '18-Unit Apartment Building',
      outcome: 'positive',
      details: {
        initial: 'Owner chose to sell AS IS to investors',
        timeline: 'Closed in 21 days',
        offer: 'Received fair cash offer based on current condition',
        benefits: [
          'No renovation costs required',
          'Avoided 12+ months of carrying costs',
          'Eliminated contractor management stress',
          'Received $1.4M cash at closing',
          'Reinvested proceeds immediately into new opportunities'
        ],
        result: 'Walked away with cash in hand',
        value: 'Avoided $800,000+ in renovation and carrying costs',
        gain: 'Net benefit of selling AS IS vs renovating'
      }
    },
    {
      title: 'Case Study 3: The Market Reality Check',
      location: 'Indianapolis, Indiana',
      property: '32-Unit Apartment Complex',
      outcome: 'negative',
      details: {
        initial: 'Owner spent $1.2M on luxury upgrades',
        upgrades: 'Granite counters, stainless appliances, hardwood floors',
        timeline: '14 months of renovation',
        issues: [
          'Market rents only supported $50/month increase',
          'Neighborhood demographics couldn\'t afford luxury pricing',
          'Competing properties charged $200/month less',
          'Struggled to lease units at projected rents',
          'Had to lower rents to match market'
        ],
        result: 'Property value increased by $240,000',
        investment: 'Total investment of $1.2M + $420,000 carrying costs',
        loss: 'Net loss of $1,380,000'
      }
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Real-World Case Studies: What Actually Happens
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            These are real scenarios we've encountered with apartment building owners. The numbers tell the story better than any theory.
          </p>
        </div>

        {/* Case Studies */}
        <div className="space-y-12 mb-16">
          {caseStudies.map((study, index) => (
            <div key={index} className={`rounded-2xl p-10 shadow-xl border-2 ${
              study.outcome === 'negative' 
                ? 'bg-gradient-to-br from-red-50 to-white border-red-200' 
                : 'bg-gradient-to-br from-teal-50 to-white border-teal-200'
            }`}>
              <div className="flex items-start gap-6 mb-8">
                <div className={`w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  study.outcome === 'negative' ? 'bg-red-600' : 'bg-teal-600'
                }`}>
                  <i className={`${study.outcome === 'negative' ? 'ri-close-circle-line' : 'ri-check-circle-line'} text-3xl text-white`}></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold mb-2">{study.title}</h3>
                  <div className="flex flex-wrap gap-4 text-gray-700">
                    <span className="flex items-center gap-2">
                      <i className="ri-map-pin-line"></i>
                      {study.location}
                    </span>
                    <span className="flex items-center gap-2">
                      <i className="ri-building-line"></i>
                      {study.property}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-xl font-bold mb-4">The Situation</h4>
                  <div className="space-y-3 text-gray-700">
                    <p><strong>Initial Decision:</strong> {study.details.initial}</p>
                    {study.details.budget && <p><strong>Budget:</strong> {study.details.budget}</p>}
                    {study.details.upgrades && <p><strong>Upgrades:</strong> {study.details.upgrades}</p>}
                    {study.details.offer && <p><strong>Offer:</strong> {study.details.offer}</p>}
                    <p><strong>Timeline:</strong> {study.details.timeline}</p>
                  </div>
                </div>

                <div>
                  <h4 className="text-xl font-bold mb-4">{study.outcome === 'negative' ? 'What Went Wrong' : 'What Went Right'}</h4>
                  <ul className="space-y-2">
                    {(study.details.issues || study.details.benefits)?.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <i className={`${study.outcome === 'negative' ? 'ri-close-line text-red-600' : 'ri-check-line text-teal-600'} text-lg mt-0.5 flex-shrink-0`}></i>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className={`mt-8 pt-8 border-t-2 ${study.outcome === 'negative' ? 'border-red-300' : 'border-teal-300'}`}>
                <h4 className="text-2xl font-bold mb-4">The Final Numbers</h4>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-white rounded-xl p-6 shadow-md">
                    <div className="text-sm text-gray-600 mb-2">Result</div>
                    <div className="text-lg font-bold">{study.details.result}</div>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-md">
                    <div className="text-sm text-gray-600 mb-2">{study.outcome === 'negative' ? 'Value Increase' : 'Cost Savings'}</div>
                    <div className="text-lg font-bold">{study.details.value}</div>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-md">
                    <div className="text-sm text-gray-600 mb-2">{study.outcome === 'negative' ? 'Net Loss' : 'Net Benefit'}</div>
                    <div className={`text-xl font-bold ${study.outcome === 'negative' ? 'text-red-600' : 'text-teal-600'}`}>
                      {study.details.loss || study.details.gain}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Image */}
        <div className="rounded-2xl overflow-hidden shadow-xl">
          <img
            src="https://public.readdy.ai/ai/img_res/ddf393289336970b41550f6e05f66354.jpg"
            alt="Real estate case studies comparison"
            className="w-full h-[500px] object-cover object-top"
          />
        </div>
      </div>
    </section>
  );
}